import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../utils/api';
import { Card, PageHeader, Btn, Badge, Modal, Input, Select } from '../components/Layout';

const TYPE_ICONS = { email: '✉️', call_script: '📞', whatsapp: '💬', ad_copy: '📢', linkedin: '💼' };
const TYPE_COLORS = { email: 'var(--accent)', call_script: 'var(--green)', whatsapp: '#25D366', ad_copy: 'var(--amber)', linkedin: '#0077B5' };
const TYPES = ['email', 'call_script', 'whatsapp', 'ad_copy', 'linkedin'];
const CATS  = ['bdm', 'recruiting', 'general'];

function TemplateModal({ template, company, mode, onClose, onSaved }) {
  const [form,       setForm]       = useState(template || { name: '', type: 'email', category: mode || 'bdm', company: company || '', subject: '', body: '', isGlobal: false });
  const [generating, setGenerating] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post('/templates/ai-generate', { type: form.type, category: form.category, company: form.company || company });
      if (r.data.subject) setForm(f => ({ ...f, subject: r.data.subject }));
      setForm(f => ({ ...f, body: r.data.body || r.data.body || '' }));
    } catch { alert('AI generation failed. Check Anthropic API key.'); }
    finally { setGenerating(false); }
  };

  const save = async () => {
    if (!form.name || !form.body) return alert('Name and body are required');
    setSaving(true);
    try {
      const payload = { ...form, company: form.isGlobal ? null : (form.company || company) };
      const r = template
        ? await api.patch(`/templates/${template._id}`, payload)
        : await api.post('/templates', payload);
      onSaved(r.data, !!template);
      onClose();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={template ? '✏️ Edit Template' : '✨ New Template'} onClose={onClose} width={680}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Input label="Template Name *" value={form.name} onChange={set('name')} placeholder="BDM Cold Intro — Step 1" />
        <Select label="Type" value={form.type} onChange={set('type')}>
          {TYPES.map(t => <option key={t} value={t}>{TYPE_ICONS[t]} {t.replace('_', ' ').toUpperCase()}</option>)}
        </Select>
        <Select label="Category" value={form.category} onChange={set('category')}>
          <option value="bdm">BDM (Software Projects)</option>
          <option value="recruiting">Recruiting (C2C/W2)</option>
          <option value="general">General</option>
        </Select>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', marginBottom: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: 'var(--mid)' }}>
            <input type="checkbox" checked={form.isGlobal} onChange={set('isGlobal')} />
            Available to all companies (Global)
          </label>
        </div>
      </div>

      {(form.type === 'email' || form.type === 'ad_copy') && (
        <Input label="Subject / Headline" value={form.subject} onChange={set('subject')} placeholder="Quick question, {{TITLE}} at {{COMPANY}}" />
      )}

      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
          <label style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5 }}>
            Body {form.type === 'call_script' ? '/ Script' : ''}
          </label>
          <Btn small onClick={generate} disabled={generating}>
            {generating ? '⚡ Generating...' : '🤖 AI Generate'}
          </Btn>
        </div>
        <textarea value={form.body} onChange={set('body')} rows={form.type === 'call_script' ? 12 : 8}
          placeholder={`Use {{LEAD_NAME}}, {{COMPANY}}, {{TITLE}}, {{SKILL}} as placeholders`}
          style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 12px', color: 'var(--text)', fontSize: 13, resize: 'vertical', lineHeight: 1.7, fontFamily: 'inherit' }} />
      </div>

      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 16 }}>
        💡 Placeholders: <code>{'{{LEAD_NAME}}'}</code> <code>{'{{FULL_NAME}}'}</code> <code>{'{{COMPANY}}'}</code> <code>{'{{TITLE}}'}</code> <code>{'{{SKILL}}'}</code> <code>{'{{PHONE}}'}</code>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Btn small onClick={onClose}>Cancel</Btn>
        <Btn primary small onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Template'}</Btn>
      </div>
    </Modal>
  );
}

function UseTemplateModal({ template, company, onClose }) {
  const [lead,   setLead]   = useState({ name: '', firmName: '', title: '', skill: '', phone: '' });
  const [filled, setFilled] = useState(null);
  const [copied, setCopied] = useState(false);
  const set = k => e => setLead(l => ({ ...l, [k]: e.target.value }));

  const preview = async () => {
    try {
      const r = await api.post(`/templates/${template._id}/use`, { lead });
      setFilled(r.data);
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal title={`📋 Use Template — ${template.name}`} onClose={onClose} width={680}>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>Fill in lead details to personalise the template:</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        <Input label="Lead First Name"       value={lead.name}     onChange={set('name')}     placeholder="James" />
        <Input label="Company Name"          value={lead.firmName} onChange={set('firmName')} placeholder="Acme Corp" />
        <Input label="Title"                 value={lead.title}    onChange={set('title')}    placeholder="CTO" />
        <Input label="Skill (for recruiting)" value={lead.skill}    onChange={set('skill')}    placeholder="Salesforce" />
      </div>
      <Btn primary small onClick={preview}>Preview Personalised</Btn>

      {filled && (
        <div style={{ marginTop: 16 }}>
          {filled.filledSubject && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 4 }}>Subject / Headline</div>
              <div style={{ background: 'var(--surface)', borderRadius: 7, padding: '10px 14px', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
                {filled.filledSubject}
                <Btn small onClick={() => copy(filled.filledSubject)}>📋 Copy</Btn>
              </div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 4 }}>Body</div>
            <div style={{ background: 'var(--surface)', borderRadius: 7, padding: '14px', fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto' }}>
              {filled.filledBody}
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <Btn primary small onClick={() => copy(filled.filledBody)}>{copied ? '✓ Copied!' : '📋 Copy Body'}</Btn>
              <Btn small onClick={() => copy(`Subject: ${filled.filledSubject || ''}\n\n${filled.filledBody}`)}>📋 Copy All</Btn>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default function Templates() {
  const { company, mode } = useOutletContext();
  const [templates,   setTemplates]   = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [filterType,  setFilterType]  = useState('');
  const [filterCat,   setFilterCat]   = useState('');
  const [search,      setSearch]      = useState('');
  const [showNew,     setShowNew]     = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [using,       setUsing]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { company };
      if (filterType) params.type     = filterType;
      if (filterCat)  params.category = filterCat;
      const r = await api.get('/templates', { params });
      setTemplates(r.data);
    } catch {} finally { setLoading(false); }
  }, [company, filterType, filterCat]);

  useEffect(() => { load(); }, [load]);

  const deleteTemplate = async id => {
    if (!window.confirm('Delete this template?')) return;
    await api.delete(`/templates/${id}`);
    setTemplates(ts => ts.filter(t => t._id !== id));
  };

  const onSaved = (tmpl, isEdit) => {
    if (isEdit) setTemplates(ts => ts.map(t => t._id === tmpl._id ? tmpl : t));
    else        setTemplates(ts => [tmpl, ...ts]);
  };

  const visible = templates.filter(t =>
    !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.body.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = TYPES.reduce((acc, type) => {
    const items = visible.filter(t => t.type === type && (!filterType || filterType === type));
    if (items.length > 0 || !filterType) acc[type] = items;
    return acc;
  }, {});

  return (
    <div style={{ padding: 28 }}>
      <PageHeader title="📚 Template Library" sub={`Reusable email, call, WhatsApp, and ad templates · ${company}`}>
        <Btn primary small onClick={() => setShowNew(true)}>+ New Template</Btn>
      </PageHeader>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search templates..."
          style={{ flex: 1, minWidth: 180, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 7, padding: '9px 14px', color: 'var(--text)', fontSize: 13, outline: 'none' }} />
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => setFilterType('')} style={{ padding: '7px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: !filterType ? 'var(--accent)' : 'var(--surface)', color: !filterType ? '#000' : 'var(--muted)', border: '1px solid var(--border)' }}>All</button>
          {TYPES.map(t => (
            <button key={t} onClick={() => setFilterType(filterType === t ? '' : t)} style={{ padding: '7px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: filterType === t ? TYPE_COLORS[t] : 'var(--surface)', color: filterType === t ? '#000' : 'var(--muted)', border: `1px solid ${filterType === t ? TYPE_COLORS[t] : 'var(--border)'}` }}>
              {TYPE_ICONS[t]} {t.replace('_', ' ')}
            </button>
          ))}
        </div>
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 7, padding: '9px 12px', color: 'var(--text)', fontSize: 13 }}>
          <option value="">All Categories</option>
          {CATS.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--muted)' }}>Loading templates...</div>
      ) : visible.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📚</div>
          <div style={{ color: 'var(--muted)', marginBottom: 20 }}>No templates yet. Create one or generate with AI.</div>
          <Btn primary onClick={() => setShowNew(true)}>+ Create First Template</Btn>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          {Object.entries(grouped).map(([type, items]) => items.length > 0 && (
            <div key={type}>
              <div style={{ fontSize: 13, fontWeight: 700, color: TYPE_COLORS[type], textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
                {TYPE_ICONS[type]} {type.replace('_', ' ')} Templates ({items.length})
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
                {items.map(tmpl => (
                  <Card key={tmpl._id} style={{ borderTop: `3px solid ${TYPE_COLORS[tmpl.type]}`, position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{tmpl.name}</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <Badge label={tmpl.category} color={tmpl.category === 'bdm' ? 'var(--accent)' : tmpl.category === 'recruiting' ? 'var(--purple)' : 'var(--muted)'} />
                          {tmpl.isGlobal && <Badge label="global" color="var(--amber)" />}
                          {tmpl.company && !tmpl.isGlobal && <Badge label={tmpl.company.split(' ')[0]} color="var(--green)" />}
                          <span style={{ fontSize: 11, color: 'var(--muted)', display: 'flex', alignItems: 'center' }}>Used {tmpl.usageCount}×</span>
                        </div>
                      </div>
                    </div>

                    {tmpl.subject && (
                      <div style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600, marginBottom: 6 }}>
                        📌 {tmpl.subject}
                      </div>
                    )}

                    <div style={{ fontSize: 12, color: 'var(--mid)', lineHeight: 1.6, marginBottom: 14, maxHeight: 80, overflowY: 'hidden', maskImage: 'linear-gradient(to bottom, black 60%, transparent)' }}>
                      {tmpl.body}
                    </div>

                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn primary small onClick={() => setUsing(tmpl)}>📋 Use</Btn>
                      <Btn small onClick={() => setEditing(tmpl)}>✏️ Edit</Btn>
                      <Btn small danger onClick={() => deleteTemplate(tmpl._id)}>🗑</Btn>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew  && <TemplateModal company={company} mode={mode} onClose={() => setShowNew(false)} onSaved={t => onSaved(t, false)} />}
      {editing  && <TemplateModal template={editing} company={company} mode={mode} onClose={() => setEditing(null)} onSaved={t => onSaved(t, true)} />}
      {using    && <UseTemplateModal template={using} company={company} onClose={() => setUsing(null)} />}
    </div>
  );
}
